<template>
    <section class="avail-daily">
        <common-notification ref="availabilityNotification" :content="notificationMessage"></common-notification>

        <availability-appointment-details-modal
            :appointment="selectedAppointmentSlot"
            :isOpen="!!selectedAppointmentSlot"
            @close="closeAppointmentDetails"
            @edit-appointment="editAppointment"
        ></availability-appointment-details-modal>

        <button type="button" class="avail-daily__back" @click="goBackToCalendar">
            <common-icon class="avail-daily__back-icon" name="arrow-left" size="tiny"></common-icon>
            {{ $t('pages.availability.index.buttons.back_calendar') }}
        </button>

        <h2 class="avail-daily__date-heading">{{ dateHeading }}</h2>

        <div class="avail-daily__columns">
            <div class="avail-daily__col">
                <h3 class="avail-daily__col-title">AM</h3>
                <div v-if="amSlots.length > 0" class="avail-daily__col-slots">
                    <div
                        v-for="(slot, idx) in amSlots"
                        :key="'am-' + idx"
                        v-clickaway="closeAllContextMenus"
                        :class="slotCardClasses(slot, 'am-' + idx)"
                        @click="handleSlotClick(slot, idx, 'am')"
                    >
                        <svg
                            v-if="selectMode && isSlotSelected(slot)"
                            class="avail-daily__slot-check"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#3b82f6" />
                            <path
                                d="M6 10.5l3 3 5-5"
                                stroke="#fff"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <div class="avail-daily__slot-main">
                            <div class="avail-daily__slot-top">
                                <span class="avail-daily__slot-time">{{ slot.startTime }}</span>
                                <button
                                    v-if="!isConfirmedSlot(slot) && !selectMode"
                                    type="button"
                                    class="avail-daily__slot-dots-text"
                                    @click.stop="toggleContextMenu(idx, 'am')"
                                >
                                    ···
                                </button>
                            </div>
                            <span v-if="slotSecondaryLabel(slot)" class="avail-daily__slot-label">
                                {{ slotSecondaryLabel(slot) }}
                            </span>
                            <span v-else-if="slot.isUnavailable && !slot.referral" class="avail-daily__slot-label">
                                {{ $t('pages.availability.index.calendar.blocked') }}
                            </span>
                        </div>
                        <referral-context-menu
                            v-if="!selectMode && openMenu === 'am-' + idx"
                            :optionProp="makeOption(slot)"
                            :showCancelled="showCancelled"
                            :availabilityMode="true"
                            type="availability"
                            @closeReferralContextMenu="openMenu = null"
                            @availability-action-start="handleAvailabilityActionStart"
                            @availability-action-end="handleAvailabilityActionEnd"
                            @availability-action-success="handleAvailabilityActionSuccess"
                            @availability-action-error="handleAvailabilityActionError"
                            @schedule-changed="emitEvent({ name: 'schedule-changed' })"
                        ></referral-context-menu>
                    </div>
                </div>
            </div>

            <div class="avail-daily__col">
                <h3 class="avail-daily__col-title">PM</h3>
                <div v-if="pmSlots.length > 0" class="avail-daily__col-slots">
                    <div
                        v-for="(slot, idx) in pmSlots"
                        :key="'pm-' + idx"
                        v-clickaway="closeAllContextMenus"
                        :class="slotCardClasses(slot, 'pm-' + idx)"
                        @click="handleSlotClick(slot, idx, 'pm')"
                    >
                        <svg
                            v-if="selectMode && isSlotSelected(slot)"
                            class="avail-daily__slot-check"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#3b82f6" />
                            <path
                                d="M6 10.5l3 3 5-5"
                                stroke="#fff"
                                stroke-width="1.8"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        <div class="avail-daily__slot-main">
                            <div class="avail-daily__slot-top">
                                <span class="avail-daily__slot-time">{{ slot.startTime }}</span>
                                <button
                                    v-if="!isConfirmedSlot(slot) && !selectMode"
                                    type="button"
                                    class="avail-daily__slot-dots-text"
                                    @click.stop="toggleContextMenu(idx, 'pm')"
                                >
                                    ···
                                </button>
                            </div>
                            <span v-if="slotSecondaryLabel(slot)" class="avail-daily__slot-label">
                                {{ slotSecondaryLabel(slot) }}
                            </span>
                            <span v-else-if="slot.isUnavailable && !slot.referral" class="avail-daily__slot-label">
                                {{ $t('pages.availability.index.calendar.blocked') }}
                            </span>
                        </div>
                        <referral-context-menu
                            v-if="!selectMode && openMenu === 'pm-' + idx"
                            :optionProp="makeOption(slot)"
                            :showCancelled="showCancelled"
                            :availabilityMode="true"
                            type="availability"
                            @closeReferralContextMenu="openMenu = null"
                            @availability-action-start="handleAvailabilityActionStart"
                            @availability-action-end="handleAvailabilityActionEnd"
                            @availability-action-success="handleAvailabilityActionSuccess"
                            @availability-action-error="handleAvailabilityActionError"
                            @schedule-changed="emitEvent({ name: 'schedule-changed' })"
                        ></referral-context-menu>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
    import dayjs from 'dayjs'
    import { mapGetters, mapMutations } from 'vuex'
    import ReferralStatusEnum from '../../../enums/ReferralStatusEnum'
    import ReferralContextMenu from '../ReferralContextMenu'
    import AvailabilityAppointmentDetailsModal from './AvailabilityAppointmentDetailsModal'

    const CONFIRMED_STATUSES = [
        ReferralStatusEnum.PATIENT_VISIT_PENDING,
        ReferralStatusEnum.INSURANCE_AUTH_PENDING,
        ReferralStatusEnum.CONSULTATION_NOTES_PENDING,
        ReferralStatusEnum.TIME_SLOTS_PROPOSED,
        ReferralStatusEnum.COMPLETED,
        ReferralStatusEnum.SCHEDULED,
    ]

    const PENDING_STATUSES = [
        ReferralStatusEnum.CREATED,
        ReferralStatusEnum.RESCHEDULE_REQUESTED,
        ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT,
        ReferralStatusEnum.RESCHEDULE_AVAILABILITY_CHANGE,
    ]

    const CANCELLED_STATUSES = [ReferralStatusEnum.CANCELLED, ReferralStatusEnum.NO_SHOW]

    export default {
        components: {
            AvailabilityAppointmentDetailsModal,
            ReferralContextMenu,
        },

        props: {
            year: { type: Number, required: true },
            month: { type: Number, required: true },
            dates: { type: Object, default: () => ({}) },
            physician: { type: Object, default: () => ({}) },
            practice: { type: Object, default: () => ({}) },
            showCancelled: { type: Boolean, default: false },
        },

        inject: {
            isProposeFlow: {
                default: false,
            },
        },

        data() {
            return {
                openMenu: null,
                selectedAppointmentSlot: null,
                pendingSlotKey: null,
                pendingSlotState: {},
                notificationMessage: '',
                localDates: {},
            }
        },

        computed: {
            ...mapGetters('availability', ['selectMode', 'selectedSlots']),
            currentDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date) : dayjs().year(this.year).month(this.month).date(1)
            },

            dateHeading() {
                return this.currentDate.format('dddd, MMMM D, YYYY')
            },

            daySlots() {
                const dateStr = this.currentDate.startOf('day').format()
                let slots = this.localDates[dateStr] || []

                // Mirror Monthly view's past-date filter (AvailabilityMonthCalendar.getSlots):
                // past dates only retain slots that have a referral; free open slots in the
                // past are irrelevant and must not be rendered as "available" cards.
                if (slots.length > 0 && this.currentDate.isBefore(dayjs(), 'date')) {
                    slots = slots.filter((s) => s.referral)
                }

                if (!this.showCancelled) {
                    slots = slots.filter((s) => !s.referral || !CANCELLED_STATUSES.includes(s.referral.status))
                }

                // Mirror Monthly view's isUnavailable "Not Available" condition
                // (AvailabilityMonthCalendarDay.showNotAvailable, condition 2):
                // when every remaining slot is either blocked (isUnavailable) or carries a
                // referral, AND there are no confirmed/pending referrals, the day is fully
                // unavailable — return [] so the Daily view shows the same empty state that
                // the Monthly badge already shows.
                if (
                    slots.length > 0 &&
                    slots.every((s) => s.isUnavailable || s.referral) &&
                    !slots.some((s) => s.referral && [...CONFIRMED_STATUSES, ...PENDING_STATUSES].includes(s.referral.status))
                ) {
                    return []
                }

                return slots
            },

            amSlots() {
                return this.daySlots.filter((s) => parseInt(s.startTime.split(':')[0]) < 12)
            },

            pmSlots() {
                return this.daySlots.filter((s) => parseInt(s.startTime.split(':')[0]) >= 12)
            },
        },

        methods: {
            ...mapMutations('availability', ['TOGGLE_SLOT']),
            isConfirmedSlot(slot) {
                return !!slot.referral && CONFIRMED_STATUSES.includes(slot.referral.status)
            },
            slotKey(slot) {
                return [slot.date, slot.startTime, slot.endTime].join('|')
            },
            isSlotSelected(slot) {
                const key = this.slotKey(slot)
                return this.selectedSlots.some((s) => `${s.date}|${s.startTime}|${s.endTime}` === key)
            },
            toggleSlotSelection(slot) {
                if (slot.referral) return
                if (this.isProposeFlow && slot.isUnavailable) return
                this.TOGGLE_SLOT(slot)
            },

            setLocalSlotAvailability(slot, isUnavailable) {
                this.$set(slot, 'isUnavailable', isUnavailable)
            },

            findSlotByKey(slotKey) {
                return this.daySlots.find((slot) => this.slotKey(slot) === slotKey)
            },

            handleSlotClick(slot, idx, period) {
                if (this.selectMode) {
                    this.toggleSlotSelection(slot)
                    return
                }

                if (this.isConfirmedSlot(slot)) {
                    this.openAppointmentDetails(slot)
                    return
                }

                this.toggleContextMenu(idx, period)
            },
            goBackToCalendar() {
                const query = { ...this.$route.query }
                query.view = 'monthly'
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query,
                    })
                    .catch(() => {})
            },

            slotCardClasses(slot, menuKey) {
                return {
                    'avail-daily__slot': true,
                    'avail-daily__slot--menu-open': this.openMenu === menuKey,
                    'avail-daily__slot--confirmed': slot.referral && CONFIRMED_STATUSES.includes(slot.referral.status),
                    'avail-daily__slot--pending': slot.referral && PENDING_STATUSES.includes(slot.referral.status),
                    'avail-daily__slot--cancelled': slot.referral && CANCELLED_STATUSES.includes(slot.referral.status),
                    'avail-daily__slot--unavailable': slot.isUnavailable && !slot.referral,
                    'avail-daily__slot--available': !slot.referral && !slot.isUnavailable,
                    'avail-daily__slot--selected': this.selectMode && this.isSlotSelected(slot),
                    'avail-daily__slot--selectable': this.selectMode && !slot.referral,
                    'avail-daily__slot--loading': this.pendingSlotKey === this.slotKey(slot),
                }
            },

            slotSecondaryUser(slot) {
                if (!slot.referral) return null

                const referral = slot.referral

                if (referral.patient && typeof referral.patient === 'object') {
                    return referral.patient
                }

                if (referral.referralInfo?.patient && typeof referral.referralInfo.patient === 'object') {
                    return referral.referralInfo.patient
                }

                if (referral.patientInfo && typeof referral.patientInfo === 'object') {
                    return referral.patientInfo
                }

                if (referral.patientData && typeof referral.patientData === 'object') {
                    return referral.patientData
                }

                if (referral.patientName) {
                    return { name: referral.patientName }
                }

                if (referral.firstName || referral.middleName || referral.lastName || referral.name) {
                    return referral
                }

                return null
            },

            slotSecondaryLabel(slot) {
                const user = this.slotSecondaryUser(slot)

                if (!user || typeof user !== 'object') {
                    return ''
                }

                const pieces = []

                if (user.firstName) pieces.push(user.firstName)
                if (user.middleName) pieces.push(user.middleName)
                if (user.lastName) pieces.push(user.lastName)

                if (pieces.length) {
                    return pieces.join(' ')
                }

                return user.name || user.patientName || ''
            },

            openAppointmentDetails(slot) {
                this.openMenu = null
                this.selectedAppointmentSlot = slot
            },

            closeAppointmentDetails() {
                this.selectedAppointmentSlot = null
            },

            editAppointment(referralId) {
                this.closeAppointmentDetails()
                if (!referralId) return
                this.$router.push({
                    name: 'referrals.received.show',
                    params: { type: 'received', id: referralId },
                    query: { action: 'reschedule' },
                })
            },

            makeOption(slot) {
                return {
                    label: [slot.startTime, slot.endTime].join(' - '),
                    value: slot,
                }
            },

            toggleContextMenu(idx, period) {
                const key = period + '-' + idx
                this.openMenu = this.openMenu === key ? null : key
            },

            closeContextMenu(idx, period) {
                const key = period + '-' + idx
                if (this.openMenu === key) {
                    this.openMenu = null
                }
                this.pendingSlotKey = null
            },

            closeAllContextMenus() {
                this.openMenu = null
                this.pendingSlotKey = null
            },

            handleAvailabilityActionStart({ slotData, action }) {
                const slotKey = this.slotKey(slotData)
                this.pendingSlotKey = slotKey
                this.pendingSlotState[slotKey] = {
                    wasUnavailable: this.findSlotByKey(slotKey)?.isUnavailable,
                }

                const slot = this.findSlotByKey(slotKey)
                if (slot) {
                    this.setLocalSlotAvailability(slot, action === 'set-unavailable')
                }
            },

            handleAvailabilityActionEnd({ slotData }) {
                const slotKey = this.slotKey(slotData)
                if (this.pendingSlotKey === slotKey) {
                    this.pendingSlotKey = null
                }
            },

            handleAvailabilityActionSuccess({ message, action, slotData }) {
                const slotKey = this.slotKey(slotData)
                delete this.pendingSlotState[slotKey]
                this.notificationMessage = message
                this.$refs.availabilityNotification.showNotification()

                if (!message && action) {
                    this.notificationMessage = action
                    this.$refs.availabilityNotification.showNotification()
                }
            },

            handleAvailabilityActionError({ message, slotData }) {
                const slotKey = this.slotKey(slotData)
                const previousState = this.pendingSlotState[slotKey]
                const slot = this.findSlotByKey(slotKey)

                if (slot && previousState) {
                    this.setLocalSlotAvailability(slot, previousState.wasUnavailable)
                }

                delete this.pendingSlotState[slotKey]
                this.notificationMessage = message
                this.$refs.availabilityNotification.showNotification()
            },
        },

        watch: {
            dates: {
                immediate: true,
                handler(newDates) {
                    this.localDates = newDates || {}
                },
            },
        },
    }
</script>

<style scoped>
    .avail-daily {
        width: 100%;
        padding: 12px 24px 0;
        position: relative;
        box-sizing: border-box;
    }

    .avail-daily__back {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 500;
        line-height: 1;
        color: #374151;
        cursor: pointer;
        transition: background 0.15s;
        margin-bottom: 24px;
    }

    .avail-daily__back:hover {
        background: #f9fafb;
    }

    .avail-daily__back-icon {
        color: #374151;
        flex-shrink: 0;
    }

    .avail-daily__date-heading {
        font-size: 18px;
        font-weight: 500;
        line-height: 1.25;
        color: #0f172a;
        margin: 0 0 28px;
    }

    .avail-daily__columns {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 42px;
    }

    .avail-daily__col {
        display: flex;
        flex-direction: column;
    }

    .avail-daily__col-title {
        font-size: 15px;
        font-weight: 500;
        line-height: 1.2;
        color: #111827;
        margin: 0 0 14px;
    }

    .avail-daily__col-empty {
        display: none;
    }

    .avail-daily__col-slots {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .avail-daily__slot {
        position: relative;
        display: flex;
        width: 100%;
        min-height: 52px;
        align-items: center;
        justify-content: flex-start;
        gap: 10px;
        padding: 10px 16px;
        box-sizing: border-box;
        border-radius: 12px;
        border: 2px dashed transparent;
        cursor: pointer;
        overflow: visible;
        transform-origin: center center;
        transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s cubic-bezier(0.22, 1, 0.36, 1),
            background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    }

    .avail-daily__slot:hover,
    .avail-daily__slot--confirmed:hover,
    .avail-daily__slot--pending:hover,
    .avail-daily__slot--cancelled:hover,
    .avail-daily__slot--unavailable:hover,
    .avail-daily__slot--available:hover,
    .avail-daily__slot--selected:hover {
        box-shadow: 0 11px 24px rgba(15, 23, 42, 0.13);
        transform: translateY(-1px) scale(1.02);
    }

    .avail-daily__slot--confirmed {
        background: #22c55e;
        border: 1px solid #16a34a;
        color: #ffffff;
    }

    .avail-daily__slot--confirmed .avail-daily__slot-time {
        font-size: 13px;
        font-weight: 500;
        line-height: 1.1;
    }

    .avail-daily__slot--confirmed .avail-daily__slot-label {
        font-size: 11px;
        font-weight: 400;
        line-height: 1.1;
        opacity: 0.9;
    }

    .avail-daily__slot--pending {
        background: linear-gradient(180deg, #fb923c 0%, #f97316 100%);
        border-color: #ea580c;
        color: #ffffff;
    }

    .avail-daily__slot--cancelled {
        background: #fde7e7;
        border-color: #fba5a5;
        border-style: dashed;
        color: #dc2626;
    }

    .avail-daily__slot--unavailable {
        background: #fde7e7;
        border-color: #fba5a5;
        border-style: dashed;
        color: #dc2626;
    }

    .avail-daily__slot--available {
        background: #ffffff;
        border-color: #d1d5db;
        color: #111827;
    }

    .avail-daily__slot-main {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
        min-width: 0;
        flex: 1;
        padding-top: 1px;
    }

    .avail-daily__slot-top {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .avail-daily__slot-time {
        font-size: 14px;
        font-weight: 400;
        line-height: 1.15;
    }

    .avail-daily__slot-dots-text {
        margin-left: auto;
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 1px;
        background: transparent;
        border: 0;
        padding: 0 6px;
        cursor: pointer;
        color: inherit;
        opacity: 0.85;
    }

    .avail-daily__slot-dots-text:hover {
        opacity: 1;
    }

    .avail-daily__slot-dots-text {
        font-size: 14px;
        font-weight: 600;
        line-height: 1;
        letter-spacing: 1px;
        background: transparent;
        border: 0;
        padding: 0 6px;
        cursor: pointer;
        color: inherit;
    }

    .avail-daily__slot-dots-text:hover {
        opacity: 0.7;
    }

    .avail-daily__slot-label {
        font-size: 11px;
        font-weight: 500;
        line-height: 1.15;
        opacity: 0.92;
        margin-top: 1px;
    }

    .avail-daily__slot-check {
        width: 24px;
        height: 24px;
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        flex-shrink: 0;
    }

    .avail-daily__slot--selectable {
        cursor: pointer;
    }

    .avail-daily__slot--selected {
        border: 2px solid #3b82f6 !important;
        background: #dbeafe !important;
        color: #1d4ed8 !important;
    }

    .avail-daily__slot--loading {
        cursor: progress;
        opacity: 0.78;
    }

    .avail-daily__slot--menu-open {
        z-index: 20;
    }

    .avail-daily__slot--selected .avail-daily__slot-main {
        padding-right: 34px;
    }

    .avail-daily__slot--selected .avail-daily__slot-time {
        color: #1d4ed8;
    }

    .avail-daily__slot--selected .avail-daily__slot-label {
        color: #2563eb;
    }

    @media (max-width: 768px) {
        .avail-daily__columns {
            grid-template-columns: 1fr;
            gap: 20px;
        }
        .avail-daily__date-heading {
            font-size: 18px;
        }
    }

    @media (max-width: 425px) {
        .avail-daily__back {
            font-size: 13px;
            padding: 6px 12px;
        }
        .avail-daily__date-heading {
            font-size: 16px;
            margin-bottom: 16px;
        }
        .avail-daily__slot {
            min-height: 62px;
            height: 62px;
            padding: 10px 14px;
        }
        .avail-daily__slot-time {
            font-size: 13px;
        }
        .avail-daily__slot-label {
            font-size: 10px;
        }
    }

    /* ReferralContextMenu rendered inside the slot card (its classes are global / child-component). */
    .avail-daily__slot ::v-deep .dropdown-menus.referral-actions {
        position: absolute;
        top: calc(100% + 4px);
        right: 8px;
        z-index: 50;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
        min-width: 220px;
        padding: 8px;
        text-align: left;
        color: #111827;
        font-weight: 500;
    }

    .avail-daily__slot ::v-deep .dropdown-menus.referral-actions .menu-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 10px;
        cursor: pointer;
        border-radius: 8px;
        font-size: 14px;
        transition: background 0.15s;
    }

    .avail-daily__slot ::v-deep .dropdown-menus.referral-actions .menu-item:hover {
        background: #f9fafb;
    }

    .avail-daily__slot ::v-deep .dropdown-menus.referral-actions .menu-item.disabled-item {
        opacity: 0.5;
        cursor: not-allowed;
    }
</style>
